import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Calculator, GraduationCap, TrendingDown, ArrowRight, Target, PiggyBank } from 'lucide-react';

const MainLanding = () => {
  const navigate = useNavigate();

  // Set page title
  React.useEffect(() => {
    document.title = 'AUMONLINE Financial Planner - Professional Planning Tools';
  }, []);

  const plannerCards = [
    {
      id: 'retirement',
      title: 'Retirement Cash Flow Planner',
      description: 'Plan your retirement with systematic investment (SIP) and withdrawal (SWP) strategies. Calculate the exact corpus needed for your desired lifestyle.',
      icon: Calculator,
      gradient: 'from-blue-800 to-green-700',
      path: '/retirement-planner',
      features: ['Inflation-adjusted calculations', 'SIP amount optimization', 'SWP withdrawal planning', 'Goal-based corpus calculation'],
      buttonText: 'Plan Retirement'
    },
    {
      id: 'education',
      title: "Children's Future Education Fund Planner",
      description: 'Secure your child\'s educational future with strategic investment planning. Calculate funding needed for higher education goals.',
      icon: GraduationCap,
      gradient: 'from-green-700 to-orange-600',
      path: '/education-planner',
      features: ['Education cost inflation', 'Course-specific planning', 'Timeline optimization', 'Investment strategy guidance'],
      buttonText: 'Plan Education'
    },
    {
      id: 'swp',
      title: 'Wealth Preservation Planner',
      description: 'Optimize your Systematic Withdrawal Plan to generate regular income from your investment corpus while preserving capital.',
      icon: TrendingDown,
      gradient: 'from-orange-600 to-yellow-600',
      path: '/swp-planner',
      features: ['Withdrawal optimization', 'Tax efficiency planning', 'Longevity analysis', 'Income sustainability'],
      buttonText: 'Plan Wealth Preservation'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-green-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex justify-center mb-4">
            <a href="https://www.aumonline.in" target="_blank" rel="noopener noreferrer">
              <img 
                src="https://customer-assets.emergentagent.com/job_retire-planner-2/artifacts/5j61poys_Logo.jpg" 
                alt="AUMONLINE Logo" 
                className="h-24 md:h-32 object-contain hover:opacity-80 transition-opacity duration-200 cursor-pointer"
              />
            </a>
          </div>
          <div className="flex justify-center mb-8">
            <a href="https://www.njwealth.in" target="_blank" rel="noopener noreferrer">
              <img 
                src="https://customer-assets.emergentagent.com/job_retire-planner-2/artifacts/0iyf1b7c_image.png" 
                alt="NJ WEALTH Logo" 
                className="h-16 md:h-20 object-contain hover:opacity-80 transition-opacity duration-200 cursor-pointer"
              />
            </a>
          </div>
          <h2 className="text-2xl md:text-3xl text-gray-600 mb-4">
            Financial Planning Solutions
          </h2>
          <p className="text-lg md:text-xl text-gray-500 max-w-3xl mx-auto leading-relaxed">
            Comprehensive financial planning tools to secure your future. From retirement planning to education funding, 
            we provide expert calculations and strategic guidance for all your financial goals.
          </p>
        </div>

        {/* Planning Tools Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto mb-16">
          {plannerCards.map((planner) => {
            const IconComponent = planner.icon;
            
            return (
              <Card key={planner.id} className="shadow-xl border-0 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 relative overflow-hidden">
                {planner.comingSoon && (
                  <div className="absolute top-4 right-4 bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-semibold z-10">
                    Coming Soon
                  </div>
                )}
                
                <CardHeader className={`bg-gradient-to-r ${planner.gradient} text-white relative`}>
                  <div className="absolute inset-0 bg-black opacity-10"></div>
                  <CardTitle className="text-xl md:text-2xl text-center flex items-center justify-center gap-3 relative z-10">
                    <IconComponent className="w-8 h-8" />
                    {planner.title}
                  </CardTitle>
                </CardHeader>
                
                <CardContent className="p-8 bg-white">
                  <p className="text-gray-700 mb-6 leading-relaxed text-center">
                    {planner.description}
                  </p>
                  
                  <div className="mb-8">
                    <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <Target className="w-5 h-5 text-blue-600" />
                      Key Features:
                    </h4>
                    <ul className="space-y-2">
                      {planner.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2 text-gray-600">
                          <div className="w-2 h-2 rounded-full bg-green-500 mt-2 flex-shrink-0"></div>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="text-center">
                    <Button
                      onClick={() => !planner.comingSoon && navigate(planner.path)}
                      disabled={planner.comingSoon}
                      className={`w-full ${
                        planner.comingSoon 
                          ? 'bg-gray-400 cursor-not-allowed' 
                          : `bg-gradient-to-r ${planner.gradient} hover:opacity-90`
                      } text-white px-8 py-4 text-lg font-semibold rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-3`}
                    >
                      {planner.comingSoon ? (
                        <>
                          <PiggyBank className="w-6 h-6" />
                          Coming Soon
                        </>
                      ) : (
                        <>
                          <Calculator className="w-6 h-6" />
                          {planner.buttonText}
                          <ArrowRight className="w-5 h-5" />
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* About Section */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <Card className="shadow-xl border-0 bg-gradient-to-r from-blue-800 to-green-700 text-white">
            <CardContent className="p-12">
              <h3 className="text-3xl font-bold mb-6">Why Choose AUMONLINE?</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
                <div>
                  <Calculator className="w-12 h-12 mx-auto mb-4 opacity-80" />
                  <h4 className="text-xl font-semibold mb-2">Precise Calculations</h4>
                  <p className="opacity-90">Advanced algorithms considering inflation, taxation, and market dynamics</p>
                </div>
                <div>
                  <Target className="w-12 h-12 mx-auto mb-4 opacity-80" />
                  <h4 className="text-xl font-semibold mb-2">Goal-Based Planning</h4>
                  <p className="opacity-90">Customized strategies aligned with your specific financial objectives</p>
                </div>
                <div>
                  <PiggyBank className="w-12 h-12 mx-auto mb-4 opacity-80" />
                  <h4 className="text-xl font-semibold mb-2">Expert Guidance</h4>
                  <p className="opacity-90">Professional insights from certified financial advisors in Mysore</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Contact Section */}
        <div className="text-center">
          <h3 className="text-2xl font-bold text-gray-800 mb-4">Need Personalized Advice?</h3>
          <p className="text-gray-600 mb-6">Our expert financial advisors are ready to help you create a comprehensive financial plan</p>
          <Button className="bg-gradient-to-r from-green-700 to-blue-800 hover:from-green-800 hover:to-blue-900 text-white px-12 py-4 text-xl font-semibold rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200"
            onClick={() => window.open('https://aumonline.in/contact-us/', '_blank')}
          >
            Contact Our Advisors
          </Button>
        </div>

        {/* SEBI Compliance Disclaimer */}
        <div className="mt-16 pt-8 border-t border-gray-300">
          <div className="text-center">
            <p className="text-lg font-bold text-red-600">
              Mutual Fund investments are subject to market risks. Read all scheme-related documents carefully before investing
            </p>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600">
            © 2025 AUMONLINE. All rights reserved. | 
            <a href="/privacy-policy" className="text-blue-600 hover:underline ml-2">Privacy Policy</a> | 
            <a href="/terms" className="text-blue-600 hover:underline ml-2">Terms of Service</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default MainLanding;